import Grid from '@mui/material/Grid';
import gastoIcon from '@assets/svgs/lineasGasto.svg';
import EsfirusTextInput from '@ui/Text-input/EsfirusTextInput';
import './LineasGasto.scss';
import EsfirusSelect from '@ui/Select/EsfirusSelect';
import { useDispatch, useSelector } from '@services/hooks/redux-hooks';
import { updateLine } from '@store/slices/lines';
import { Capitulo } from '@models/obra';
import { useEffect, useMemo, useState } from 'react';
import { erpService } from '@services/erp';
import useDebounce from '@services/hooks/useDebounce';
import { configurationService } from '@services/configApp';
import { getInternalCompany } from '@helpers/companyData';
import { UserWithDataParsed } from '@models/user';
import { getUserId } from '@services/hooks/UserHook';
import { getExternalData } from '@services/hooks/UserHook';
import { Empresa } from '@appTypes/common';

interface Gasto {
  Articulo: string;
  Ambito: number;
  Descripcion: string;
  Auxiliar: string;
  Marca: string;
  Coste: number;
  Gasto: boolean;
  Publicable: boolean;
  Referencia: string;
  Servicio: boolean;
  Tarifa: number;
}

function LineasGasto(props: any) {
  const dispatch = useDispatch();
  const lineas = useSelector((state) => (state as any).lines);
  const [showPrice, setShowPrice] = useState(false);

  const { lineasParte, buscadorObra, ftp_method } =
    configurationService.getConfig();
  const selectedobra = useSelector((state) => (state as any).selectedobra).obra;
  const gastosServicios = useSelector(
    (state) => (state as any).articulosGastosServicios
  ).list;
  const empresas = useSelector((state) => (state as any).empresas).list;
  const users = useSelector((state) => state.users.list);
  const { tarifa } = JSON.parse(getExternalData());

  const cantidadDebounced = useDebounce(lineas?.list[props.index]?.amount, 500);

  const facturable =
    lineas?.list[props.index]?.facturable ??
    Boolean(
      lineasParte.lineaGastoFacturableByDefault && selectedobra.tipo === 'A'
    );

  const empleadosOptions = useMemo(() => {
    const empleadosOfInternalCompany = users.filter((em) => {
      const isInInternalArray = em.data.internal.some(
        (v) => v.empresa == getInternalCompany()
      );
      return isInInternalArray;
    });

    const options = empleadosOfInternalCompany.map((empleado) => ({
      label: `${empleado.nombre} ${empleado.apellidos}`,
      value: empleado.id,
    }));

    options.sort((a, b) => a.label.localeCompare(b.label));

    return options;
  }, [users]);

  const allowToSelectEmpleado = useMemo(
    () =>
      lineasParte.manoObraSeleccionarEmpleado &&
      !buscadorObra.filtroObraShowEmpresa,
    [lineasParte, buscadorObra]
  );

  const [amountIncorrect, setAmountIncorrect] = useState(false);
  const [precioIncorrect, setPrecioIncorrect] = useState(false);

  useEffect(() => {
    const updateSalesPricing = async () => {
      const gastoRef = lineas.list[props.index]?.gasto;
      const selectedGasto = gastosServicios.find(
        (el: Gasto) => el.Articulo === gastoRef
      );
      const salesPricingData = await fetchSalesPricingData(
        selectedGasto,
        Number(cantidadDebounced) ?? 1
      );

      dispatch(
        updateLine({
          ...lineas.list[props.index],
          data_sales_pricing: salesPricingData?.data_sales_pricing ?? {},
        })
      );
    };

    const goToFetchSalesPricing =
      selectedobra.Administracion &&
      lineas?.list?.[props.index]?.gasto &&
      ftp_method === 'endpoint';
    if (goToFetchSalesPricing) {
      updateSalesPricing();
    }
  }, [cantidadDebounced, lineas.list[props.index]?.gasto]);

  const getChapterOptions = (): any[] => {
    if (!selectedobra.capitulosByPresupuesto) {
      return [];
    }

    const ret: any = selectedobra.capitulosByPresupuesto?.map(
      (cap: Capitulo) => ({
        ...cap,
        label: cap.label,
        value: cap.Capitulo,
      })
    );
    return ret;
  };

  const getGastos = () => {
    const empresaSelected = empresas.find(
      (emp: Empresa) => emp.Empresa === selectedobra.company
    );
    if (!empresaSelected) return [];

    return gastosServicios
      .filter(
        ({ Gasto, Ambito, Tarifa }: Gasto) =>
          Gasto &&
          Ambito === Number(empresaSelected.Ambito) &&
          (!tarifa || Tarifa === Number(tarifa))
      )
      .sort((a: Gasto, b: Gasto) => a.Articulo.localeCompare(b.Articulo))
      .map(({ Articulo }: Gasto) => ({ value: Articulo, label: Articulo }));
  };

  useEffect(() => {
    const asyncFunc = async () => {
      const selectedGasto = gastosServicios.find(
        (el: Gasto) => el.Articulo === lineas.list[props.index]?.gasto
      );

      if (!selectedGasto) return;

      const costeObra = selectedGasto?.Coste
      const costeAdmin = ftp_method === "endpoint" ? await fetchSalesPricingData(selectedGasto, lineas.list[props.index]?.amount ?? 1) : { data_sales_pricing: { price: 0 } }

      if ((selectedobra?.Administracion && !costeAdmin?.data_sales_pricing?.price) || (!selectedobra?.Administracion && !costeObra)) {
        setShowPrice(true)
      } else {
        setShowPrice(false);
      }
    };

    const setInitialData = () => {
      let userWorkerValue: UserWithDataParsed | undefined =
        lineas?.list[props.index]?.userWorker ?? null;

      //ponemos el empleado de la mano de obra por defecto
      if (!lineas?.list[props.index]?.userWorker && allowToSelectEmpleado) {
        const userCabecera = users.find(
          (user) => user.id == (selectedobra?.userEmpleadoId ?? getUserId())
        );
        userWorkerValue = userCabecera;
      }

      let chapter = lineas?.list[props.index]?.chapter ?? undefined
      if (lineas.anchorFields?.active) {
        chapter = lineas.list?.slice()?.reverse()?.find((l: any) => !!l.chapter)?.chapter
      }

      dispatch(
        updateLine({
          ...lineas.list[props.index],
          facturable,
          userWorker: userWorkerValue,
          chapter,
        })
      );
    };

    asyncFunc();
    setInitialData();
  }, []);

  const fetchSalesPricingData = async (servicio: any, quantity: any) => {
    if (!servicio || !quantity) return {};

    const salesResponse = await erpService.getSalesPricing(
      servicio,
      selectedobra,
      quantity,
      lineas.list[props.index]
    );
    const sales = salesResponse?.data?.data ?? {};
    return sales;
  };

  return (
    <Grid
      className="line-services-container"
      container
      spacing={1}
      alignItems={'center'}
    >
      <Grid
        className="flex-row"
        item
        xs={12}
        md={2}
        alignItems={'center'}
        justifyContent={'center'}
      >
        <img className="logo mr-10" src={gastoIcon} alt="service" />
        <h4>GS</h4>
      </Grid>
      <Grid item xs={12} md={10} container spacing={2}>
        {allowToSelectEmpleado && (
          <Grid item xs={12} md={3}>
            <label>Empleado</label>
            <EsfirusSelect
              options={empleadosOptions}
              change={(e: any) => {
                dispatch(
                  updateLine({
                    ...lineas.list[props.index],
                    userWorker: users.find((user) => user.id == e),
                  })
                );
              }}
              value={lineas.list[props.index]?.userWorker?.id}
              disabled={!props.editable || !!lineas.list[props.index]?.idLinea}
              placeholder="Seleccionar"
            />
          </Grid>
        )}
        {!selectedobra.Administracion &&
          selectedobra.SolCapitulo &&
          selectedobra.capitulosByPresupuesto.length !== 0 &&
          !Boolean(props.isPedido) && (
            <Grid item xs={12} md={3}>
              <label>Capítulo</label>
              <EsfirusSelect
                disabled={!props.editable}
                optionsStyle={{ textAlign: 'left', fontWeight: 'bold' }}
                options={getChapterOptions()}
                value={lineas?.list[props.index]?.chapter?.Capitulo}
                change={(e: any) => {
                  const selectedChapter =
                    selectedobra.capitulosByPresupuesto.find(
                      (el: Capitulo) => el.Capitulo === e
                    );

                  dispatch(
                    updateLine({
                      ...lineas.list[props.index],
                      chapter: selectedChapter,
                    })
                  );
                }}
                placeholder={'Capítulo'}
                modified={
                  !!lineas.list[props.index]?.validationModifications?.datos
                    ?.chapter
                }
              ></EsfirusSelect>
            </Grid>
          )}

        <Grid item xs={12} md={3}>
          <label>Gasto</label>
          <EsfirusSelect
            options={getGastos()}
            disabled={!props.editable}
            value={lineas.list[props.index]?.gasto}
            change={(ref: string) => {
              const selectedGasto = gastosServicios.find(
                (el: Gasto) => el.Articulo === ref
              );

              if (!selectedGasto.Coste) {
                setShowPrice(true);
              } else {
                setShowPrice(false);
              }

              const precio = selectedGasto.Coste ?? 0;

              dispatch(
                updateLine({
                  ...lineas.list[props.index],
                  gasto: selectedGasto.Articulo,
                  description: selectedGasto.Descripcion,
                  amount: 1,
                  ambito: selectedGasto.Ambito,
                  tarifa: selectedGasto.Tarifa,
                  totalPrice: precio ?? 0,
                  precio: precio ?? 0,
                })
              );
            }}
            modified={
              !!lineas.list[props.index]?.validationModifications?.datos?.gasto
            }
            required={true}
          ></EsfirusSelect>
        </Grid>
        <Grid item xs={12} md={3}>
          <label>Descripción</label>
          <EsfirusTextInput
            fullWidth
            disabled={!props.editable}
            onChange={(e: any) => {
              dispatch(
                updateLine({
                  ...lineas.list[props.index],
                  description: e.target.value,
                })
              );
            }}
            value={lineas?.list[props.index]?.description}
            modified={
              !!lineas.list[props.index]?.validationModifications?.datos
                ?.description
            }
          />
        </Grid>

        <Grid item xs={6} md={2}>
          <label>Cantidad</label>
          <EsfirusTextInput
            type="number"
            value={lineas?.list[props.index]?.amount}
            InputProps={{ inputProps: { min: 1 } }}
            fullWidth
            disabled={!props.editable}
            onChange={(e: any) => {
              if (Number(e.target.value ?? 0) <= 0) setAmountIncorrect(true);
              else setAmountIncorrect(false);

              const newCantidad = e.target.value;

              const totalPrice =
                Number(lineas.list[props.index].precio) * Number(newCantidad);

              dispatch(
                updateLine({
                  ...lineas.list[props.index],
                  amount: newCantidad,
                  totalPrice,
                })
              );
            }}
            modified={
              !!lineas.list[props.index]?.validationModifications?.datos?.amount
            }
          />
          {amountIncorrect && (
            <p style={{ color: 'red', marginTop: '5px', fontSize: '12px' }}>
              La cantidad tiene que ser mayor que 0.
            </p>
          )}
        </Grid>
        {showPrice && (
          <Grid item xs={6} md={2}>
            <label>Precio</label>
            <EsfirusTextInput
              type="number"
              fullWidth
              value={lineas?.list[props.index]?.precio}
              InputProps={{ inputProps: { min: 0 } }}
              disabled={!showPrice || !props.editable}
              onChange={(e: any) => {
                if (Number(e.target.value ?? 0) < 0) setPrecioIncorrect(true);
                else setPrecioIncorrect(false);

                const totalPrice =
                  Number(e.target.value ?? 0) *
                  Number(lineas.list[props.index].amount ?? 0);

                dispatch(
                  updateLine({
                    ...lineas.list[props.index],
                    precio: e.target.value,
                    precioExterno: e.target.value,
                    totalPrice,
                  })
                );
              }}
              modified={
                !!lineas.list[props.index]?.validationModifications?.datos
                  ?.precio
              }
            />
            {precioIncorrect && (
              <p style={{ color: 'red', marginTop: '5px', fontSize: '12px' }}>
                El precio no puede ser menor que 0.
              </p>
            )}
          </Grid>
        )}

        {selectedobra.tipo === 'A' && (
          <Grid item xs={6} md={2}>
            <label className="containerCheckbox">
              <input
                type="checkbox"
                disabled={!lineasParte.opcionFacturableActive}
                checked={facturable}
                onChange={() => {
                  dispatch(
                    updateLine({
                      ...lineas.list[props.index],
                      facturable: !facturable,
                    })
                  );
                }}
              />
              <span>Facturable</span>
              <span className="checkmark"></span>
            </label>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
}

export default LineasGasto;
